import {
  ChevronDownIcon,
  ChevronUpIcon,
  ClipboardCopyIcon,
} from '@radix-ui/react-icons';
import {
  Box,
  Button,
  Card,
  Code,
  DataList,
  Flex,
  IconButton,
  Popover,
  Spinner,
} from '@radix-ui/themes';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { InstallationContext } from 'components/machine/dialogs/installation/context';
import { MachineContext } from 'contexts/machine.context';
import { parseISO } from 'date-fns';
import { format } from 'date-fns-tz';
import { t } from 'i18next';
import {
  LOCAL_DATETIME_FORMAT,
  LOCAL_TIMEZONE,
} from 'lib_ts/enums/datetime.enums';
import { InstallStep } from 'lib_ts/enums/installation';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { ICollectionShotGroup } from 'lib_ts/interfaces/training/i-machine-shot';
import { sumBy } from 'lodash';
import { useContext, useEffect, useState } from 'react';
import { DataCollectionsService } from 'services/data-collections.service';

export const InstallationDetails = () => {
  const { machine } = useContext(MachineContext);
  const { btc, rt, installation, tryCancel } = useContext(InstallationContext);

  const [openDetails, setOpenDetails] = useState(false);

  const [btcAgg, setBtcAgg] = useState<ICollectionShotGroup[]>([]);
  const [btcLoading, setBtcLoading] = useState(false);

  // load btc shots
  useEffect(() => {
    if (!openDetails) {
      return;
    }

    if (!btc) {
      return;
    }

    setBtcLoading(true);

    DataCollectionsService.getShotSummaries(btc._id)
      .then((result) => setBtcAgg(result))
      .finally(() => setBtcLoading(false));
  }, [openDetails, btc, machine]);

  const [rtAgg, setRtAgg] = useState<ICollectionShotGroup[]>([]);
  const [rtLoading, setRtLoading] = useState(false);

  // load rt shots
  useEffect(() => {
    if (!openDetails) {
      return;
    }

    if (!rt) {
      return;
    }

    setRtLoading(true);

    DataCollectionsService.getShotSummaries(rt._id)
      .then((result) => setRtAgg(result))
      .finally(() => setRtLoading(false));
  }, [openDetails, rt, machine]);

  return (
    <Popover.Root onOpenChange={(open) => setOpenDetails(open)}>
      <Popover.Trigger>
        <Button
          size={RADIX.BUTTON.SIZE.MODAL}
          color={RADIX.COLOR.SECONDARY}
          variant="soft"
        >
          {t('common.details')}
          {openDetails ? <ChevronUpIcon /> : <ChevronDownIcon />}
        </Button>
      </Popover.Trigger>
      <Popover.Content asChild>
        <Card style={{ minWidth: '200px' }}>
          <Flex
            p="1" // +3 (from card) = 4
            direction="column"
            gap="2"
          >
            <DataList.Root orientation="vertical" size="1">
              <DataList.Item>
                <DataList.Label>{t('common.ball-type')}</DataList.Label>
                <DataList.Value>{installation.ball_type}</DataList.Value>
              </DataList.Item>
              <DataList.Item>
                <DataList.Label>{t('settings.user')}</DataList.Label>
                <DataList.Value>{installation.started_by}</DataList.Value>
              </DataList.Item>
              <DataList.Item>
                <DataList.Label>{t('Started On')}</DataList.Label>
                <DataList.Value>
                  {format(
                    parseISO(installation.start_date),
                    LOCAL_DATETIME_FORMAT,
                    { timeZone: LOCAL_TIMEZONE }
                  )}
                </DataList.Value>
              </DataList.Item>

              {installation.end_date && (
                <DataList.Item>
                  <DataList.Label>{t('Finished On')}</DataList.Label>
                  <DataList.Value>
                    {format(
                      parseISO(installation.end_date),
                      LOCAL_DATETIME_FORMAT,
                      { timeZone: LOCAL_TIMEZONE }
                    )}
                  </DataList.Value>
                </DataList.Item>
              )}

              {(!installation.standalone ||
                installation.standalone === InstallStep.BallType) &&
                btc && (
                  <>
                    <DataList.Item>
                      <DataList.Label>
                        {t('common.calibration-id')}
                      </DataList.Label>
                      <DataList.Value>
                        <Flex
                          gap="2"
                          justify="between"
                          align="center"
                          style={{ width: '100%' }}
                        >
                          <Code color={RADIX.COLOR.NEUTRAL}>
                            {btc.collectionID}
                          </Code>

                          <IconButton
                            className="btn-floating"
                            color={RADIX.COLOR.NEUTRAL}
                            size="1"
                            variant="soft"
                            onClick={() => {
                              if (!btc) {
                                return;
                              }

                              navigator.clipboard.writeText(btc.collectionID);

                              NotifyHelper.success({
                                message_md: t('common.x-copied-to-clipboard', {
                                  x: btc.collectionID,
                                }),
                              });
                            }}
                          >
                            <ClipboardCopyIcon />
                          </IconButton>
                        </Flex>
                      </DataList.Value>
                    </DataList.Item>

                    <DataList.Item>
                      <DataList.Label>
                        {t('common.calibration-shots')}
                      </DataList.Label>
                      <DataList.Value>
                        {btcLoading ? (
                          <Spinner />
                        ) : (
                          sumBy(btcAgg, (m) => m.total)
                        )}
                      </DataList.Value>
                    </DataList.Item>
                  </>
                )}

              {(!installation.standalone ||
                installation.standalone === InstallStep.Repeatability) &&
                rt && (
                  <>
                    <DataList.Item>
                      <DataList.Label>
                        {t('common.repeatability-id')}
                      </DataList.Label>
                      <DataList.Value>
                        <Flex
                          gap="2"
                          justify="between"
                          align="center"
                          style={{ width: '100%' }}
                        >
                          <Code color={RADIX.COLOR.NEUTRAL}>
                            {rt.collectionID}
                          </Code>

                          <IconButton
                            className="btn-floating"
                            color={RADIX.COLOR.NEUTRAL}
                            size="1"
                            variant="soft"
                            onClick={() => {
                              if (!rt.collectionID) {
                                return;
                              }

                              navigator.clipboard.writeText(rt.collectionID);

                              NotifyHelper.success({
                                message_md: t('common.x-copied-to-clipboard', {
                                  x: rt.collectionID,
                                }),
                              });
                            }}
                          >
                            <ClipboardCopyIcon />
                          </IconButton>
                        </Flex>
                      </DataList.Value>
                    </DataList.Item>
                    <DataList.Item>
                      <DataList.Label>
                        {t('common.repeatability-shots')}
                      </DataList.Label>
                      <DataList.Value>
                        {rtLoading ? (
                          <Spinner />
                        ) : (
                          <Box
                            title={`Aiming: ${sumBy(
                              rtAgg,
                              (m) => m.aiming
                            )}\nAnalysis: ${sumBy(rtAgg, (m) => m.analysis)}`}
                          >
                            {sumBy(rtAgg, (m) => m.total)}
                          </Box>
                        )}
                      </DataList.Value>
                    </DataList.Item>
                  </>
                )}
            </DataList.Root>

            {!installation.end_date && (
              <Button
                size="1"
                variant={RADIX.BUTTON.VARIANT.SECONDARY}
                color={RADIX.COLOR.DANGER}
                onClick={() => tryCancel()}
              >
                Cancel
              </Button>
            )}
          </Flex>
        </Card>
      </Popover.Content>
    </Popover.Root>
  );
};
